// Recipient PWA home. Mobile-first for HT family members: send a request, and
// — the key action — confirm delivery with the PIN once a merchant marks an
// order delivered. Wired to real orders (requests.list for the recipient role).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShoppingBag, CheckCircle2, Truck, Loader2, ShieldCheck, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PortalShell } from '../components/shared/PortalShell';
import { Card, SectionHeader, StatusPill, EmptyState } from '../components/shared/primitives';
import { Button } from '../components/stripe-ui/Button';
import { DisputePanel } from '../components/shared/DisputePanel';
import { DeliveryProof } from '../components/shared/DeliveryProof';
import { RECIPIENT_NAV } from './_navs';
import { useRecipientNav } from '../lib/pendingAction';
import { requests, type ServiceRequestWithItems } from '../lib/api';
import { DELIVERY_CODE_LENGTH, normaliseDeliveryCode, isCompleteDeliveryCode } from '../lib/deliveryCode';


const TERMINAL = ['confirmed', 'cancelled', 'refunded'];

interface RecipientProfile { full_name?: string; city?: { name: string } }

export function RecipientHome() {
  const { user } = useAuth();
  const rp = (user?.recipient_profile ?? {}) as RecipientProfile;
  const firstName = (rp.full_name ?? 'there').split(' ')[0];

  const q = useQuery({ queryKey: ['recipient', 'requests'], queryFn: () => requests.list() });
  const all = q.data ?? [];
  const active = all.filter((r) => !TERMINAL.includes(r.status));
  const toConfirm = active.filter((r) => r.status === 'delivered');
  const others = active.filter((r) => r.status !== 'delivered');
  const recent = all.filter((r) => r.status === 'confirmed').slice(0, 3);

  // Badge the Home tab when this role owes an action, so a phone user
  // sees it from any screen rather than only on the right page.
  const nav = useRecipientNav(RECIPIENT_NAV);

  return (
    <PortalShell
      nav={nav}
      activeId="home"
      title={`Bonjou, ${firstName}`}
      subtitle="Your family in the diaspora can buy what you need."
    >
      <div className="space-y-5">
        {/* Hero + the code, side by side. Web Portal v2 leads with these two:
            who you are and the one number that matters. */}
        <div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))]">
          <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 to-brand-700 p-7 text-white">
            <div aria-hidden className="absolute inset-x-0 top-0 flex h-1">
              <span className="w-1/2 bg-[var(--color-interactive)]" />
              <span className="w-1/2 bg-[var(--color-haiti-red)]" />
            </div>
            {rp.city?.name && (
              <p className="text-[11.5px] font-extrabold uppercase tracking-[1.4px] text-[#9FBBF5]">
                {rp.city.name}
              </p>
            )}
            <h1 className="mt-2.5 text-[28px] md:text-[34px] font-extrabold tracking-[-1px]">
              Bonjou, {firstName}
            </h1>
            <p className="mt-2 max-w-[420px] text-[15px] leading-[1.5] text-[#C9D8FB]">
              {toConfirm.length > 0
                ? 'Something has arrived. Check it, then give the driver your code.'
                : others.length > 0
                  ? 'Your family has something on the way to you.'
                  : 'Ask your family for what you need — they see it straight away.'}
            </p>
            <Link
              to="/recipient/plan"
              className="mt-5 inline-flex rounded-[14px] bg-white px-5 py-3 text-[14px] font-extrabold text-brand-600 transition-colors hover:bg-brand-50"
            >
              Ask for something
            </Link>
          </section>

          <DeliveryCodeCard request={toConfirm[0] ?? others[0] ?? null} />
        </div>

        {/* Confirming is what releases the merchant's payment, so it keeps its
            own card rather than being folded into a list row. */}
        {toConfirm.length > 0 && (
          <Card className="border-[var(--color-success-border)] bg-[var(--color-success-bg)]/40">
            <SectionHeader
              title="Did everything arrive?"
              subtitle="Check it first. Entering your code is what pays the merchant."
            />
            <div className="space-y-3 mt-1">
              {toConfirm.map((r) => <ConfirmRow key={r.id} r={r} />)}
            </div>
          </Card>
        )}

        <Card>
          <SectionHeader title="On the way to you" subtitle="Awaiting a quote, payment, or delivery." />
          {q.isLoading ? (
            <div className="text-center py-8 text-[var(--color-muted)] text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : others.length === 0 && toConfirm.length === 0 ? (
            <EmptyState icon={ShoppingBag} title="Nothing on the way" body="Ask your family above and it will show up here." />
          ) : (
            <div className="divide-y divide-[var(--color-hairline)] -mx-1 px-1">
              {others.map((r) => (
                <div key={r.id} className="py-3 flex items-start gap-3">
                  <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl shrink-0 bg-brand-50 text-brand-600">
                    <Truck className="w-4 h-4" aria-hidden="true" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-extrabold text-[var(--color-ink)]">
                      {r.title || r.category?.label || 'On its way'}
                    </p>
                    <p className="mt-0.5 text-[12.5px] text-[var(--color-muted)]">
                      {r.cart_item_count} item{r.cart_item_count === 1 ? '' : 's'}
                      {r.city?.name ? ` · ${r.city.name}` : ''}
                    </p>
                  </div>
                  <StatusPill status={r.status} />
                </div>
              ))}
            </div>
          )}
        </Card>

        {recent.length > 0 && (
          <Card>
            <SectionHeader
              title="Recent deliveries"
              actions={<Link to="/recipient/history" className="text-[12px] font-semibold text-brand-600 hover:underline">History →</Link>}
            />
            <div className="divide-y divide-[var(--color-hairline)] -mx-1 px-1">
              {recent.map((r) => (
                <div key={r.id} className="py-3 flex items-center gap-3">
                  <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg shrink-0 bg-[var(--color-success-bg)] text-[var(--color-success-fg)]">
                    <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-[var(--color-ink)]">
                      {r.title || r.category?.label || 'Delivered'}
                    </p>
                    <p className="text-[11.5px] text-[var(--color-muted)]">Delivered &amp; confirmed</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <p className="text-[11px] text-[var(--color-muted)] inline-flex items-center gap-1 justify-center">
          <ShieldCheck className="w-3 h-3" /> Add VoyeLakay to your home screen for one-tap access.
        </p>
      </div>
    </PortalShell>
  );
}

/**
 * The delivery code, at the size the design gives it.
 *
 * This is the recipient's half of the escrow promise: nobody is paid until
 * they hand it over. The page previously only offered a small input to TYPE
 * it into, which serves one of the two real moments — the other is reading it
 * aloud to a driver at the door, where a 16px field in a list row is the
 * wrong shape entirely.
 *
 * The server sends `delivery_pin` to the recipient (and the customer) by
 * design; the merchant never receives it.
 */
function DeliveryCodeCard({ request }: { request: ServiceRequestWithItems | null }) {
  const code = request?.delivery_pin ?? null;

  if (!request || !code) {
    return (
      <section className="flex flex-col justify-center rounded-[22px] border border-[var(--color-border)] bg-white p-6">
        <p className="text-[12px] font-extrabold uppercase tracking-[0.8px] text-[var(--color-muted)]">
          Your delivery code
        </p>
        <p className="mt-3 text-[13.5px] leading-[1.55] text-[var(--color-secondary)]">
          A code appears here once your family pays for an order. You give it to
          the driver only when everything has arrived — that is what releases
          payment to the merchant.
        </p>
      </section>
    );
  }

  return (
    <section className="flex flex-col rounded-[22px] border border-[var(--color-border)] bg-white p-6">
      <p className="text-[12px] font-extrabold uppercase tracking-[0.8px] text-[var(--color-muted)]">
        Your delivery code
      </p>
      {/* Grouped in pairs: eight unbroken digits are hard to read aloud, and
          reading it aloud is exactly what this is for. */}
      <p className="mt-3 font-mono text-[34px] md:text-[48px] font-medium leading-none tracking-[0.18em] text-[var(--color-ink)]">
        {code.replace(/(.{2})/g, '$1 ').trim()}
      </p>
      <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--color-secondary)]">
        Give this code to the driver <strong>only when everything has arrived</strong>.
        That is what releases payment to the merchant.
      </p>
      <div className="flex-1" />
      <p className="mt-4 font-mono text-[12px] text-[var(--color-muted)]">
        REQ-{String(request.id).padStart(4, '0')}
        {request.city?.name ? ` · ${request.city.name}` : ''}
      </p>
    </section>
  );
}

function ConfirmRow({ r }: { r: ServiceRequestWithItems }) {
  const qc = useQueryClient();
  const [pin, setPin] = useState('');
  const confirm = useMutation({
    mutationFn: () => requests.confirmReceipt(r.id, pin.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipient', 'requests'] });
      toast.success('Delivery confirmed — mèsi! Your receipt is on the way.');
    },
    onError: (e: Error) => toast.error(e.message || 'That code is incorrect.'),
  });

  return (
    <div className="rounded-lg border border-[var(--color-success-border)] bg-white p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[12px] text-[#063B9E]">REQ-{String(r.id).padStart(4, '0')}</span>
        <span className="text-[13px] font-semibold text-[var(--color-ink)]">· {r.category?.label ?? '—'}</span>
        <StatusPill status={r.status} />
      </div>
      {/* The recipient is vouching that this arrived, and their code releases
          the merchant's payment. The hand-off photo is the evidence they are
          judging against — the handoff requires customer, recipient and admin
          all to see it, and the recipient was the one role that could not. */}
      <div className="mt-2">
        <DeliveryProof requestId={r.id} />
      </div>

      <p className="mt-3 text-[12px] text-[var(--color-secondary)] inline-flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-[var(--color-muted)]" /> Enter the delivery code you received (by WhatsApp or SMS):</p>
      <div className="mt-2 flex items-center gap-2">
        <input
          value={pin}
          onChange={(e) => setPin(normaliseDeliveryCode(e.target.value))}
          inputMode="numeric"
          placeholder={`${DELIVERY_CODE_LENGTH}-digit code`}
          aria-label={`${DELIVERY_CODE_LENGTH}-digit delivery code`}
          className="w-36 px-3 py-2 text-[16px] font-mono tracking-[0.3em] text-center border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-success-border)]/40"
          // The code is always DELIVERY_CODE_LENGTH digits. This used to submit at 4, so a
          // half-typed code hit the server and came back "incorrect" — which
          // reads as "the code you were given is wrong", not "keep typing".
          onKeyDown={(e) => { if (e.key === 'Enter' && isCompleteDeliveryCode(pin)) confirm.mutate(); }}
        />
        <Button tone="primary" onClick={() => confirm.mutate()} loading={confirm.isPending} disabled={!isCompleteDeliveryCode(pin)}>
          <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm receipt
        </Button>
      </div>

      {/* The other half of the rule: confirming releases the merchant's money,
          so refusing to confirm has to be possible from the same screen. The
          server already accepts a recipient-opened dispute (DisputeController
          checks $isRecipient and records opened_by='recipient'); only this
          mount was missing, so the recipient's sole option was to confirm a
          delivery they might not have received. */}
      <div className="mt-3">
        <DisputePanel requestId={r.id} status={r.status} canOpen />
      </div>
    </div>
  );
}
